// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  DateRangeRounded,
  NumbersRounded,
  TextFieldsRounded,
  ToggleOffRounded,
} from '@/constants/hardfin-standard-icons';
import { useTimeout } from '@/hooks/use-timeout';
import { createItemField } from '@/items/api/create-item-field';
import { ItemFieldBooleanFormatSelector } from '@/items/components/item-field-boolean-format-selector';
import { ItemFieldDateTimeFormatSelector } from '@/items/components/item-field-date-time-format-selector';
import { ItemFieldFormatSelector } from '@/items/components/item-field-format-selector';
import { ItemNumericFieldFormatSelector } from '@/items/components/item-numeric-field-format-selector';
import {
  ItemFieldType,
  itemFieldTypeSchema,
} from '@/items/constants/item-field-types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  styled,
  toggleButtonClasses,
  toggleButtonGroupClasses,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for field type validation.
 */
const typeSchema = z.enum([...itemFieldTypeSchema.options], {
  message: 'Type is required',
});

/**
 * Form validation schema using Zod.
 * Enforces required fields with appropriate error messages.
 */
const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: typeSchema,
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  /**
   * Controls the visibility of the dialog.
   * True shows the dialog, false hides it.
   */
  isOpen: boolean;

  /**
   * The display order for the new field.
   * Used to position the field within its section.
   */
  order: number;

  /**
   * The section number for the new field.
   * Used to group related fields together.
   */
  section: number;

  /**
   * The key of the item to add the field to.
   * Used for API operations and query invalidation.
   */
  itemKey: string;

  /**
   * Callback fired when the dialog should close.
   * Called after successful creation or when user cancels.
   */
  onClose: () => void;
}

/**
 * Styled toggle button group for field type selection.
 * Customizes Material-UI's ToggleButtonGroup for a cleaner look.
 */
const FieldTypeToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonClasses.root}.${toggleButtonGroupClasses.grouped}`]: {
    border: 0,
    margin: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
  },
}));

/**
 * A dialog component for creating new item fields.
 *
 * Features:
 * - Form validation using react-hook-form and Zod
 * - Tabbed interface for field type selection
 * - Visual card selection for text format options
 * - Automatic form reset on close
 * - Smooth transition animations
 * - Automatic query invalidation on success
 * - Handles duplicate field name errors
 *
 * Form Fields:
 * - Name: Required text input for field label
 * - Type: Required selection from field type tabs
 * - Format: Required selection from format cards (when applicable)
 */
export function CreateItemFieldDialog(props: Props) {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [selectedTab, setSelectedTab] = useState<string>('text');
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null
  );
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Initialize form with react-hook-form
  const {
    formState,
    reset,
    register,
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: 'text',
    },
  });

  const fieldType = watch('type');

  // Set up delayed form reset for smooth transitions
  const timeout = useTimeout(reset, theme.transitions.duration.standard);

  // Reset form and tab selection when dialog opens
  useEffect(() => {
    if (props.isOpen) {
      setSelectedTab('text');
      setValue('type', 'text');
      setInlineErrorMessage(null);
      setShowSnackbar(false);
      clearErrors();
    }
  }, [props.isOpen, setValue, clearErrors]);

  // Set up mutation for creating item fields
  const mutation = useMutation({
    mutationFn: (data: FormData) => {
      return createItemField(props.itemKey, {
        label: data.name,
        fieldType: data.type,
        order: props.order,
        section: props.section,
      });
    },
    onError: (error: Error) => {
      clearErrors();
      setInlineErrorMessage(null);
      setShowSnackbar(false);
      if (isPlanLimitError(error)) {
        return;
      }

      if (!(error instanceof AxiosError)) {
        captureException(error);
        setShowSnackbar(true);
        return;
      }
      const apiError = handleAPIError(error);
      if (apiError.errorType === 'UNIQUE_VIOLATION') {
        setError('name', {
          type: 'manual',
          message: 'A field with this name already exists',
        });
        return;
      }
      if (apiError.errorType === 'product_field_label_required') {
        setError('name', {
          type: 'manual',
          message: 'Name is required',
        });
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setInlineErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setShowSnackbar(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['item', props.itemKey],
      });

      close();
    },
  });

  /**
   * Handles tab change and updates the form field type.
   */
  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    if (newValue === null) {
      return;
    }
    setSelectedTab(newValue);

    switch (newValue) {
      case 'text':
        setValue('type', 'text');
        break;
      case 'number':
        setValue('type', 'integer');
        break;
      case 'date_time':
        setValue('type', 'date');
        break;
      case 'toggle':
        setValue('type', 'boolean');
        break;
    }
  };

  /**
   * Handles format selection change.
   */
  const handleFormatChange = (value: ItemFieldType) => {
    setValue('type', value);
  };

  /**
   * Handles form submission when validation passes.
   * Creates the field with the specified name, type, order, and section.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    mutation.mutate(data);
  };

  /**
   * Handles dialog closure with a delayed form reset
   * to ensure smooth transition animations.
   */
  const close = () => {
    setInlineErrorMessage(null);
    setShowSnackbar(false);
    timeout.start();
    props.onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      transitionDuration={theme.transitions.duration.standard}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={showSnackbar}
        message="An error occurred while adding the field."
        onClose={() => setShowSnackbar(false)}
      />
      <DialogTitle>Add field</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {inlineErrorMessage && (
            <Alert severity="error" onClose={() => setInlineErrorMessage(null)}>
              {inlineErrorMessage}
            </Alert>
          )}
          <TextField
            size="small"
            label="Name"
            error={Boolean(formState.errors.name)}
            helperText={formState.errors.name?.message}
            InputProps={register('name')}
          />
          <Stack spacing={2} alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">Type</Typography>
              <Paper variant="outlined">
                <FieldTypeToggleButtonGroup
                  exclusive
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label="field type selection"
                >
                  <ToggleButton value="text" sx={{ minWidth: 110 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TextFieldsRounded fontSize="small" />
                      <span>Text</span>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="number" sx={{ minWidth: 110 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <NumbersRounded fontSize="small" />
                      <span>Number</span>
                    </Stack>
                  </ToggleButton>
                  <ToggleButton value="date_time" sx={{ minWidth: 110 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DateRangeRounded fontSize="small" />
                      <span>Date/time</span>
                    </Stack>
                  </ToggleButton>
                  <Tooltip
                    arrow
                    title="Boolean types coming soon"
                    placement="top"
                  >
                    <span>
                      <ToggleButton value="toggle" sx={{ minWidth: 110 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ToggleOffRounded fontSize="small" />
                          <span>Toggle</span>
                        </Stack>
                      </ToggleButton>
                    </span>
                  </Tooltip>
                </FieldTypeToggleButtonGroup>
              </Paper>
            </Stack>
            {selectedTab === 'text' && (
              <ItemFieldFormatSelector
                value={fieldType}
                onChange={handleFormatChange}
              />
            )}
            {selectedTab === 'number' && (
              <ItemNumericFieldFormatSelector
                value={fieldType}
                onChange={handleFormatChange}
              />
            )}
            {selectedTab === 'date_time' && (
              <ItemFieldDateTimeFormatSelector
                value={fieldType}
                onChange={handleFormatChange}
              />
            )}
            {selectedTab === 'toggle' && (
              <ItemFieldBooleanFormatSelector
                value={fieldType}
                onChange={handleFormatChange}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          disabled={mutation.isPending}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
